import * as React from 'react';
import { Typography } from '@mui/material';
import { formatPrice } from '@europrocurement/l2d-utils';
import EllipsisCell from './components/EllipsisCell';

const getDefaultLabelFromField = (key: string): string => {
    // Example : 'fooBarBaz' => 'Foo bar baz'
    const arrayOfFormattedWords = key.split(/(?=[A-Z])/).map((word, index) => {
        if (index === 0) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word.toLowerCase();
    });

    return arrayOfFormattedWords.join(' ');
};

export const shrinkString = (value: string, limit: number) => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    let result;
    const appendDots = value.length > limit;
    result = value.substring(0, limit);
    result = appendDots ? `${result}...` : result;
    return result;
};

export const getStringColumn = <T,>(
    field: keyof T,
    labelValue?: string,
    // Déprécié : la troncature est désormais gérée à la largeur de la colonne (EllipsisCell).
) => {
    const label = labelValue ?? getDefaultLabelFromField(field as string);

    return {
        label,
        displayLabel: true,
        render: (item: T) => <EllipsisCell text={item[field] as string} />,
        isDisplayed: true,
        sortable: false,
        field,
        // Contourne le plafond `maxWidth: 200px` de la datatable : la troncature
        // (fondu + tooltip) est gérée par EllipsisCell à la vraie largeur de la colonne.
        sx: { maxWidth: 'none' },
    };
};

export const getPriceColumn = <T,>(field: keyof T, labelValue?: string) => {
    const label = labelValue ?? getDefaultLabelFromField(field as string);
    return {
        label,
        displayLabel: true,
        render: (item: T) => {
            const value = item[field] as string;
            const priceValue = parseFloat(value);

            if (priceValue === 0) {
                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        (offert)
                    </Typography>
                );
            }
            if (!priceValue) {
                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        - - -
                    </Typography>
                );
            }

            return (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {formatPrice(priceValue)} €
                </Typography>
            );
        },
        isDisplayed: true,
        sortable: false,
        field,
    };
};

export const displayCustomValue = <T,>(
    field: keyof T,
    formatFunction: (fieldValue: string | [] | T) => string,
    labelValue?: string,
    // Déprécié : la troncature est désormais gérée à la largeur de la colonne (EllipsisCell).
) => {
    const label = labelValue ?? getDefaultLabelFromField(field as string);

    return {
        label,
        displayLabel: true,
        render: (item: T) => <EllipsisCell text={formatFunction(item[field] as string)} />,
        isDisplayed: true,
        sortable: false,
        field,
        // Contourne le plafond `maxWidth: 200px` de la datatable : la troncature
        // (fondu + tooltip) est gérée par EllipsisCell à la vraie largeur de la colonne.
        sx: { maxWidth: 'none' },
    };
};
